import { Check, Megaphone, Search, MousePointerClick, PhoneCall, Star, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const META_FEATURES = [
  "Lead campaigns", "Retargeting", "Local advertising", "Video advertising",
  "Offer testing", "Creative testing", "Audience testing",
];

const GOOGLE_FEATURES = [
  "Google Search", "Call campaigns", "Local campaigns",
  "Keyword strategy", "Conversion tracking", "Retargeting",
];

export const Platforms = () => (
  <section id="services" className="relative py-12 sm:py-16">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Our Core Expertise</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
          Two Platforms. One Goal:{" "}
          <span className="text-purple-500">More Customers.</span>
        </h2>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Reveal className="h-full">
          <div
            data-testid="platform-meta"
            className="flex h-full flex-col rounded-3xl border border-[#272732] bg-[#121217] p-8 transition-colors duration-300 hover:border-purple-500/50 sm:p-10"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-600/15 text-purple-400">
                <Megaphone className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Meta Ads</h3>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-400">Facebook + Instagram</p>
              </div>
            </div>

            {/* Meta-style ad creative mock */}
            <div className="mt-7 overflow-hidden rounded-2xl border border-white/10 bg-[#0B0B10]">
              <div className="flex items-center gap-2.5 px-4 py-3">
                <span className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700" />
                <div>
                  <p className="text-xs font-bold text-white">Your Business</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Sponsored</p>
                </div>
              </div>
              <div className="flex h-36 items-center justify-center bg-gradient-to-br from-purple-700 via-purple-600 to-fuchsia-600">
                <p className="px-6 text-center font-display text-xl font-extrabold tracking-tight text-white">
                  Your offer. Their feed.
                </p>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <p className="text-xs font-bold text-white">This week only — book now</p>
                <span className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-[11px] font-bold text-white">
                  Get Quote <MousePointerClick className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>

            <p className="mt-7 text-sm leading-relaxed text-zinc-400 md:text-base">
              We put your best offers in front of the people most likely to
              become your next customers.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
              {META_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm font-medium text-zinc-300">
                  <Check className="h-4 w-4 shrink-0 text-purple-400" strokeWidth={3} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="h-full">
          <div
            data-testid="platform-google"
            className="flex h-full flex-col rounded-3xl border border-[#272732] bg-[#121217] p-8 transition-colors duration-300 hover:border-purple-500/50 sm:p-10"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-600/15 text-purple-400">
                <Search className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Google Ads</h3>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-400">Search + High-Intent Traffic</p>
              </div>
            </div>

            {/* Google search result mock */}
            <div className="mt-7 rounded-2xl border border-white/10 bg-[#0B0B10] p-4">
              <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-3">
                <Search className="h-4 w-4 shrink-0 text-zinc-500" />
                <p className="text-sm text-zinc-300">[service] near me</p>
              </div>
              <div className="mt-4 rounded-xl border border-purple-500/30 bg-purple-600/5 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Sponsored</p>
                <p className="mt-1 text-sm font-bold text-purple-300">Your Business — Top Rated Local Service</p>
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                  Fast response. Free quotes. Call today and talk to a real person.
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium text-zinc-400">
                  <span className="flex items-center gap-1 text-amber-400">
                    <Star className="h-3 w-3 fill-amber-400" /> 4.9
                  </span>
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Your City</span>
                  <span className="flex items-center gap-1 text-purple-300"><PhoneCall className="h-3 w-3" /> Call Now</span>
                </div>
              </div>
            </div>

            <p className="mt-7 text-sm leading-relaxed text-zinc-400 md:text-base">
              When someone is already searching for what you sell, we help make
              sure your business is one of the options they see.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
              {GOOGLE_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm font-medium text-zinc-300">
                  <Check className="h-4 w-4 shrink-0 text-purple-400" strokeWidth={3} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
